import { UserManagementLookups } from "./user-management.lookups";
import { UserRequest, portalRoles } from "./user-management.entities";

export type FormMode = "create" | "approve-new" | "approve-update" | "edit-user";
export type FieldType = 'text' | 'select' | 'checkbox' | 'display';

export interface FormField {
    id: keyof UserRequest;
    label: string;
    type: FieldType;
    options?: { value: any; text: string }[];
    required: boolean;
    defaultChecked?: boolean;
}


export interface FormLayoutField {
    id: keyof UserRequest;
    col: number;
    hidden?: boolean;
}

export interface FormSection {
    title: string;
    col: number;
    rows?: FormLayoutField[][];
}

export const MainUserForm: FormField[] = [
    { id: 'Email', label: 'Email', type: 'text', required: true },
    { id: 'FirstName', label: 'First Name', type: 'text', required: true },
    { id: 'LastName', label: 'Last Name', type: 'text', required: true },
    { id: 'Organization', label: 'Organization', type: 'text', required: true },
    { id: 'TLCRole', label: 'TLC Role', type: 'text', required: false },
    { id: 'AffiliationId', label: 'Affiliation', type: 'select', required: true },
    { id: 'SiteInfo', label: 'Site Name/Number the user entered', type: 'display', required: false },
    { id: 'SiteId', label: 'Site', type: 'select', required: false },
];

export const UserFormLayout: FormSection[] = [
    {
        title: "Users Information",
        col: 12,
        rows: [
            [{ id: 'Email', col: 12 }],
            [{ id: 'FirstName', col: 6 }, { id: 'LastName', col: 6 }],
            [{ id: 'Organization', col: 12 }],
            [{ id: 'TLCRole', col: 12 }],
        ]
    },
    {
        title: "Please Select Primary Affiliation and Site",
        col: 12,
        rows: [
            [{ id: 'AffiliationId', col: 12 }],
            [{ id: 'SiteInfo', col: 12 }],
            [{ id: 'SiteId', col: 12 }]
        ]
    },
];

export class UserFormBuilder {

    static build(mode: FormMode, request?: any, currentUser?: any, affiliations?: { Id: number; Name: string }[], roles: portalRoles[] = [], sites: { Id: number; SiteNumber: string; SiteName: string }[] = []): string {
        if (mode === "approve-update") {
            return this.buildComparison(request, currentUser);
        }

        const layout = this.buildLayout(roles);
        const fieldMap = this.buildFieldMap(roles);

        let html = `<form id="user-form" class="request-form-group">`;       

        html += `
            <div class="container-fluid modal-body">
                <div class="text-danger px-3 d-none" id="all-error-message">Please complete all required fields*.</div>
        `;

        layout.forEach(section => {
            html += `
                <div class="mt-3 form-section" data-section="${section.title}">
                    <div class="form-section-heading request-subheader">
                        ${section.title}
                    </div>
            `;

            section.rows?.forEach(row => {
                html += `<div class="row">`;
                row.forEach(layoutField => {
                    const field = fieldMap[layoutField.id as string];
                    if (field) {
                        html += this.renderField(field, layoutField, request, currentUser, affiliations, sites, mode);
                    }
                });
                html += `</div>`;
            });

            html += `</div>`;
        });

        if (request?.Id) {
            html += `<input type="hidden" id="Id" value="${request.Id}" />`;
        }

        html += `
                <input name="__RequestVerificationToken" type="hidden" value="@requestToken" />
            </div>
        </form>`;

        return html;
    }

    private static normalizeUpdateRequest(request: any): any {
        if (!request) return {};

        const normalized: any = { ...request };

        return normalized;
    }

    private static buildComparison(requestData?: any, currentUser?: any): string {
        if (typeof requestData === "string") {
            try {
                requestData = JSON.parse(requestData);
            } catch {
                requestData = {};
            }
        }

        requestData = this.normalizeUpdateRequest(requestData);
        let html = `<div class="update-request-comparison container-fluid">
                   <div class="form-section-heading request-subheader mt-3">
                        Please review the reqeuest for: ${currentUser.Email}
                    </div>
        `;
      
        if (currentUser.TypeId == 1) {            
            const fieldMap = Object.fromEntries(MainUserForm.map(f => [f.id, f]));

            for (const key of Object.keys(requestData)) {
                const field: FormField = fieldMap[key] ?? {
                    id: key as keyof UserRequest,
                    label: key,
                    type: 'text',
                    required: false,
                };
                html += this.renderComparisonField(field, requestData, currentUser);
            }
        } else if (currentUser.TypeId == 2) {
            const rolesToAdd: string[] = (requestData.RolesToAdd ?? []).sort((a, b) => a.localeCompare(b));
            const rolesToRemove: string[] = (requestData.RolesToRemove ?? []).sort((a, b) => a.localeCompare(b));
            if (rolesToAdd.length > 0) {
                html += `
        <div class="col-12 mt-4 form-section access-header">
            <div class="form-section-heading request-subheader">
                Access to Add
            </div>
            <div class="row access-row">
    `;
                for (const roleName of rolesToAdd) {
                    const field: FormField = {
                        id: roleName as keyof UserRequest,
                        label: roleName,
                        type: 'checkbox',
                        required: false,
                    };
                    html += this.renderRoleComparisonField(field, currentUser, 'add');
                }
                html += `</div></div>`;
            }
            if (rolesToRemove.length > 0) {
                html += `
        <div class="col-12 mt-4 form-section access-header">
            <div class="form-section-heading request-subheader">
                Access to Remove
            </div>
            <div class="row access-row">
    `;
                for (const roleName of rolesToRemove) {
                    const field: FormField = {
                        id: roleName as keyof UserRequest,
                        label: roleName,
                        type: 'checkbox',
                        required: false,
                    };
                    html += this.renderRoleComparisonField(field, currentUser, 'remove');
                }
                html += `</div></div>`;
            }
        }

        html += `</div>`;
        html += `<input type="hidden" id="Id" value="${currentUser.Id}" />`;
        html += `</div>`;

        return html;
    }

    private static buildLayout(roles: portalRoles[]): FormSection[] {
        const layout: FormSection[] = [...UserFormLayout];

        if (roles.length > 0) {
            layout.push({
                title: "Please Select which areas of the portal to allow Access to",
                col: 6,
                rows: this.buildRoles(roles),
            });
        }

        return layout;
    }

    private static buildRoles(roles: portalRoles[]): FormLayoutField[][] {
        const rows: FormLayoutField[][] = [];
        const sorted = [...roles].sort((a, b) => a.DisplayName.localeCompare(b.DisplayName));

        for (let i = 0; i < sorted.length; i += 2) {
            const row: FormLayoutField[] = [
                { id: sorted[i].Name as keyof UserRequest, col: 6 }
            ];
            if (sorted[i + 1]) {
                row.push({ id: sorted[i + 1].Name as keyof UserRequest, col: 6 });
            }
            rows.push(row);
        }

        return rows;
    }

    private static buildFieldMap(roles: portalRoles[]): Record<string, FormField> {
        const map: Record<string, FormField> = Object.fromEntries(
            MainUserForm.map(f => [f.id, f])
        );

        roles.forEach(role => {
            map[role.Name] = {
                id: role.Name as keyof UserRequest,
                label: role.DisplayName,
                type: 'checkbox',
                required: false,
                defaultChecked: role.Name === 'Public-Comment',
            };
        });

        return map;
    }

    private static renderComparisonField(field: FormField, request: any, currentUser: any, affiliations?: { Id: number; Name: string }[]): string {
        const requestedRaw = request?.[field.id];
        const currentRaw = this.getCurrentValue(field.id, currentUser);

        const currentValue = this.getDisplayValue(field, currentRaw, affiliations);
        const requestedValue = this.getDisplayValue(field, requestedRaw, affiliations);

        return `
            <div class="comparison-row mb-3 access-row">
                <div class="comparison-label font-bold">${field.label}</div>
                <div class="row mx-0">
                    <div class="col-md-6 col-12">
                        <div class="p-3 border rounded bg-light">
                            <div class="small text-muted">Current</div>
                            <div>${currentValue || "-"}</div>
                        </div>
                    </div>
                    <div class="col-md-6 col-12">
                        <div class="p-3 border rounded border-warning">
                            <div class="small text-muted">Requested</div>
                            <div>${requestedValue || "-"}</div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    private static renderRoleComparisonField(field: FormField, currentUser: any, section: 'add' | 'remove'): string {      
        const message = section === 'add'
            ? `${field.label}`
            : `${field.label}`;

        const badgeClass = section === 'add' ? 'border-success' : 'border-danger';

        return `
        <div class="comparison-row mr-3 mb-3">
             <div class="p-3 border rounded ${badgeClass}">                      
                 <div>${message}</div>
            </div>
        </div>
    `;
    }

    private static renderField(field: FormField, layout: FormLayoutField, request: any, currentUser: any, affiliations?: { Id: number; Name: string }[], sites: { Id: number; SiteNumber: string; SiteName: string }[] = [], mode?: FormMode): string {
        const requestedValue = request?.[field.id];
        const currentValue = currentUser?.[field.id];

        let valueToShow: any;

        if (currentUser) {
            valueToShow = requestedValue !== undefined ? requestedValue : currentValue ?? '';
        } else if (request) {
            valueToShow = requestedValue ?? '';
        } else {
            valueToShow = '';
        }
        if (field.id === 'SiteInfo' && (mode === 'create' || mode === 'edit-user')) {
            return '';
        }

        let isChecked = false;
        if (field.type === 'checkbox') {
            if (request && request[field.id] !== undefined) {
                isChecked = Number(request[field.id]) === 1;
            }
            else {
                const rolesArray: string[] = request?.Roles ?? currentUser?.Roles ?? [];
                isChecked = rolesArray.some(r => r.toLowerCase() === String(field.id).toLowerCase());
            }

            // Default to checked if no existing context and field has defaultChecked
            if (!isChecked && !request && !currentUser && field.defaultChecked) {
                isChecked = true;
            }
        }

        let html = `
            <div class="col-md-${layout.col} col-12" data-field-container="${field.id}">
                <div class="form-group">
        `;

        if (field.type === 'text' && field.required === true) {
            html += `
                <label for="Input_${field.id}" class="font-bold required">
                    ${field.label}*
                </label>
                <input
                    id="Input_${field.id}"
                    class="form-control ${field.id.toLowerCase()}-input"
                    value="${valueToShow ?? ''}"
                />
                <span class="text-danger d-none validation-msg">
                    ${field.label.toLowerCase() === "email"
                    ? "Please enter a valid email address."
                    : `Please enter ${field.label.toLowerCase()}.`}
                </span>
            `;
        }

        if (field.type === 'text' && field.required === false) {
            html += `
                <label for="${field.id}" class="font-bold">
                    ${field.label}
                </label>
                <input
                    id="Input_${field.id}"
                    class="form-control"
                    value="${valueToShow ?? ''}"
                />
                <span class="text-danger d-none validation-msg">
                    Please enter ${field.label.toLowerCase()}.
                </span>
            `;
        }

        if (field.type === 'select') {
            let options: { value: any; text: string }[] = [];

            if (field.id === 'AffiliationId' && affiliations) {
                options = affiliations.map(a => ({ value: a.Id, text: a.Name }));
            } else if (field.id === 'SiteId' && sites) {
                options = sites.map(s => ({ value: s.Id, text: `${s.SiteNumber} - ${s.SiteName}` }));
            } else {
                options = field.options || [];
            }

            if (field.id === 'SiteId') {
                html += `
                <label class="font-bold">
                    ${field.label}
                </label>
                <select id="Input_${field.id}" class="form-control">
                    <option value="">-- Select ${field.label} --</option>
            `;
            } else {
                html += `
                <label class="font-bold required">
                    ${field.label}*
                </label>
                <select id="Input_${field.id}" class="form-control">
                    <option value="">-- Select ${field.label} --</option>
            `;
            }
          
            options.forEach(o => {
                const sel = Number(o.value) === Number(valueToShow) ? 'selected' : '';
                html += `<option value="${o.value}" ${sel}>${o.text}</option>`;
            });

            html += `
                </select>
                <span class="text-danger d-none validation-msg">
                    Please select ${field.label.toLowerCase()}.
                </span>
            `;
        }
        if (field.type === 'display' && field.id === 'SiteInfo') {
            html += `
            <label class="font-bold">${field.label}</label>
            <div class="form-control-plaintext border rounded px-3 py-2 bg-light">
                ${valueToShow || '<span class="text-muted">Not provided</span>'}
            </div>
        `;
            html += `</div></div>`;
            return html;
        }

        if (field.type === 'checkbox') {
            html += `
                <div class="form-check">
                    <input type="checkbox" name="${field.id}" id="Input_${field.id}" class="form-check-input" ${isChecked ? 'checked' : ''}/>
                    <label for="Input_${field.id}" class="form-check-label">${field.label}</label>
                </div>
            `;
        }

        html += `</div></div>`;

        return html;
    }

    private static getDisplayValue(
        field: FormField,
        value: any,
        affiliations?: { Id: number; Name: string }[]
    ): string {
        if (field.type === "checkbox") {
            return Number(value) === 1 ? "Yes" : "No";
        }

        if (field.id === "AffiliationId") {
            if (!value) return "";
            const match = affiliations?.find(a => Number(a.Id) === Number(value));
            return match?.Name ?? `${value}`;
        }

        return value ?? "";
    }

    private static getCurrentValue(fieldId: keyof UserRequest, currentUser: any): any {
        if (!currentUser) return null;

        switch (fieldId) {
            case "TLCRole":
                return currentUser.TLCRole;
            default:
                return currentUser[fieldId];
        }
    }
 
}